import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useUpdateDocumentAsOf } from '@/hooks/react-query/useDocuments';
import type { Document } from '@/types/document';

interface EditAsOfDialogProps {
  document: Document;
  onClose: () => void;
}

export function EditAsOfDialog({ document, onClose }: EditAsOfDialogProps) {
  const [asOf, setAsOf] = useState(document.as_of ?? '');
  const updateAsOf = useUpdateDocumentAsOf();

  const handleSave = () => {
    updateAsOf.mutate(
      { documentId: document.id, asOf: asOf || null },
      { onSuccess: onClose }
    );
  };

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit As of Date</DialogTitle>
          <DialogDescription>{document.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1 py-4">
          <Label htmlFor="edit-as-of">As of date</Label>
          <DateInput
            id="edit-as-of"
            className="w-44"
            value={asOf}
            onChange={setAsOf}
          />
          <p className="text-xs text-muted-foreground">
            Empty uses the upload date.
          </p>
          {document.portal_visible && (
            <p className="text-xs text-amber-700">
              Investors see this document. The change reorders their list.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateAsOf.isPending}>
            {updateAsOf.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
